/** @internal – import from `@/store/useDepartmentStore` instead. */
import { env } from "@/config/env";
import { authHeaders, buildErrorMessage } from "./utils";
import { sseEvents } from "../../events";

export type SseDebugEvent = {
  phase?: unknown;
  status?: unknown;
  progress?: {
    track?: unknown;
    completed?: unknown;
    total?: unknown;
  };
  error?: unknown;
};

export function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

export function createPipelineDebugLogger(pipeline: string, sessionId: string, endpoint: string) {
  const enabled = process.env.NODE_ENV !== "production";
  const prefix = `[coagent/${pipeline}]`;
  let lastPhaseStatus = "";
  let lastProgressSnapshot = "";
  let lastProgressLogAt = 0;
  let parseWarningLogged = false;

  const info = (message: string, meta?: Record<string, unknown>) => {
    if (!enabled) return;
    if (meta) {
      console.info(`${prefix} ${message}`, meta);
      return;
    }
    console.info(`${prefix} ${message}`);
  };

  const warn = (message: string, meta?: Record<string, unknown>) => {
    if (!enabled) return;
    if (meta) {
      console.warn(`${prefix} ${message}`, meta);
      return;
    }
    console.warn(`${prefix} ${message}`);
  };

  const error = (message: string, meta?: Record<string, unknown>) => {
    if (!enabled) return;
    if (meta) {
      console.error(`${prefix} ${message}`, meta);
      return;
    }
    console.error(`${prefix} ${message}`);
  };

  return {
    requestStart(extra?: Record<string, unknown>) {
      info(`[API] POST ${endpoint} start`, { sessionId, ...(extra || {}) });
      sseEvents.publish({
        type: "sse:request-start",
        pipeline,
        sessionId,
        endpoint,
        ...(extra ? { meta: extra } : {}),
      });
    },
    requestResponse(res: Response) {
      info(`[API] POST ${endpoint} response`, { sessionId, status: res.status, ok: res.ok });
      sseEvents.publish({
        type: "sse:request-response",
        pipeline,
        sessionId,
        endpoint,
        status: res.status,
        ok: res.ok,
      });
    },
    requestTransportError(err: unknown) {
      error(`[API] POST ${endpoint} failed`, { sessionId, error: toErrorMessage(err) });
      sseEvents.publish({
        type: "sse:request-transport-error",
        pipeline,
        sessionId,
        endpoint,
        message: toErrorMessage(err),
      });
    },
    requestHttpError(status: number, message: string, details?: unknown) {
      error(`[API] POST ${endpoint} non-OK`, {
        sessionId,
        status,
        message,
        details,
      });
      sseEvents.publish({
        type: "sse:request-http-error",
        pipeline,
        sessionId,
        endpoint,
        status,
        message,
        ...(details !== undefined ? { details } : {}),
      });
    },
    streamConnected() {
      info("[API] SSE stream connected", { sessionId });
      sseEvents.publish({ type: "sse:stream-connected", pipeline, sessionId });
    },
    streamClosed() {
      info("[API] SSE stream closed", { sessionId });
      sseEvents.publish({ type: "sse:stream-closed", pipeline, sessionId });
    },
    event(sseEvent: SseDebugEvent) {
      const phase = typeof sseEvent.phase === "string" ? sseEvent.phase : "unknown";
      const status = typeof sseEvent.status === "string" ? sseEvent.status : "unknown";
      const phaseStatus = `${phase}:${status}`;
      if (phaseStatus !== lastPhaseStatus) {
        info("[SSE] phase update", { sessionId, phase, status });
        sseEvents.publish({
          type: "sse:phase-update",
          pipeline,
          sessionId,
          phase,
          status,
        });
        lastPhaseStatus = phaseStatus;
      }

      const progress = sseEvent.progress;
      if (progress && typeof progress === "object") {
        const track = typeof progress.track === "string" ? progress.track : phase;
        const completed = Number(progress.completed ?? 0);
        const total = Number(progress.total ?? 0);
        const snapshot = `${phase}:${track}:${completed}/${total}`;
        const now = Date.now();
        const shouldLog =
          snapshot !== lastProgressSnapshot &&
          (completed === 0 || completed === total || now - lastProgressLogAt >= 2000);
        if (shouldLog) {
          info("[SSE] progress", { sessionId, phase, track, completed, total });
          sseEvents.publish({
            type: "sse:progress",
            pipeline,
            sessionId,
            phase,
            track,
            completed,
            total,
          });
          lastProgressSnapshot = snapshot;
          lastProgressLogAt = now;
        }
      }

      if (phase === "error") {
        warn("[SSE] pipeline error event", { sessionId, error: sseEvent.error });
        sseEvents.publish({
          type: "sse:error",
          pipeline,
          sessionId,
          error: sseEvent.error,
        });
      }
    },
    parseError(rawLine: string, parseErr: unknown) {
      if (parseWarningLogged) return;
      parseWarningLogged = true;
      warn("[SSE] skipped unparseable event", {
        sessionId,
        error: toErrorMessage(parseErr),
        preview: rawLine.slice(0, 160),
      });
    },
  };
}

export type PipelineDebugLogger = ReturnType<typeof createPipelineDebugLogger>;

export interface SSERunnerConfig<TInput> {
  pipeline: string;
  endpoint: string;
  sessionId: string;
  buildBody: (sessionId: string, input: TInput) => Record<string, unknown>;
  handleEvent: (
    event: Record<string, unknown>,
    debug: PipelineDebugLogger,
  ) => void;
  errorPrefix: string;
  signal?: AbortSignal;
  onStreamEnd?: () => boolean;
  debugMeta?: Record<string, unknown>;
}

/**
 * Phase 8 commit 80: pipelines publish typed events on `sseEvents` and
 * no longer receive a `callbacks` mutator bag. Subscribers in
 * `features/department/events/*Subscriber.ts` translate events into
 * store mutations.
 */
export async function runSSEStream<TInput>(
  input: TInput,
  config: SSERunnerConfig<TInput>,
): Promise<void> {
  const {
    pipeline,
    endpoint,
    sessionId,
    buildBody,
    handleEvent,
    errorPrefix,
    signal,
    onStreamEnd,
    debugMeta,
  } = config;
  const debug = createPipelineDebugLogger(pipeline, sessionId, endpoint);
  debug.requestStart(debugMeta);

  let res: Response;
  try {
    const url = endpoint.startsWith("/core/") ? `${env.coreUrl}${endpoint}` : endpoint;
    res = await fetch(url, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify(buildBody(sessionId, input)),
      signal,
    });
  } catch (err) {
    debug.requestTransportError(err);
    throw err;
  }
  debug.requestResponse(res);

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `${errorPrefix} failed` }));
    const message = err.error || `${errorPrefix} failed (${res.status})`;
    debug.requestHttpError(res.status, message, err.details);
    throw new Error(buildErrorMessage(message, err.details));
  }

  const reader = res.body?.getReader();
  if (!reader) {
    debug.requestTransportError("No response stream");
    throw new Error("No response stream");
  }
  debug.streamConnected();

  const decoder = new TextDecoder();
  let buffer = "";

  const parseSSELine = (line: string) => {
    const dataLine = line.replace(/^data: /, "").trim();
    if (!dataLine) return;

    try {
      const event = JSON.parse(dataLine);
      debug.event(event);
      handleEvent(event, debug);
    } catch (parseErr) {
      debug.parseError(dataLine, parseErr);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        parseSSELine(line);
      }
    }

    if (buffer.trim()) {
      parseSSELine(buffer);
    }

    if (onStreamEnd) {
      onStreamEnd();
    }
  } finally {
    debug.streamClosed();
  }
}
